import { UserManager, User } from 'oidc-client-ts';

// Import UserService für automatische Benutzerregistrierung
let UserService: any = null;
// Lazy Loading des UserService um zirkuläre Abhängigkeiten zu vermeiden
const getUserService = async () => {
  if (!UserService) {
    const module = await import('./UserService');
    UserService = module.default;
  }
  return UserService;
};

const AUTHENTIK_URL = process.env.REACT_APP_AUTHENTIK_URL || 'https://auth.example.com';
const CLIENT_ID = process.env.REACT_APP_AUTHENTIK_CLIENT_ID || 'telefonbuch';
const APPLICATION_SLUG = process.env.REACT_APP_AUTHENTIK_APPLICATION_SLUG || 'telefonbuch';
const REDIRECT_URI = `${window.location.protocol}//${window.location.host}/callback`;
const POST_LOGOUT_REDIRECT_URI = `${window.location.protocol}//${window.location.host}/`;

// Debug-Logging für Konfiguration
console.log('=== Authentik Configuration ===');
console.log('AUTHENTIK_URL:', AUTHENTIK_URL);
console.log('CLIENT_ID:', CLIENT_ID);
console.log('APPLICATION_SLUG:', APPLICATION_SLUG);
console.log('REDIRECT_URI:', REDIRECT_URI);
console.log('POST_LOGOUT_REDIRECT_URI:', POST_LOGOUT_REDIRECT_URI);
console.log('Authority URL:', `${AUTHENTIK_URL}/application/o/${APPLICATION_SLUG}/`);
console.log('================================');

const userManagerSettings = {
  authority: `${AUTHENTIK_URL}/application/o/${APPLICATION_SLUG}/`,
  client_id: CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
  response_type: 'code',
  scope: 'openid profile email groups',
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.protocol}//${window.location.host}/silent-callback`,
  filterProtocolClaims: true,
  loadUserInfo: false,
};

class AuthService {
  private userManager: UserManager;
  private user: User | null = null;

  constructor() {
    this.userManager = new UserManager(userManagerSettings);
    this.userManager.events.addUserLoaded(this.userLoaded);
    this.userManager.events.addUserUnloaded(this.userUnloaded);
    this.userManager.events.addAccessTokenExpiring(this.accessTokenExpiring);
    this.userManager.events.addAccessTokenExpired(this.accessTokenExpired);
    this.userManager.events.addSilentRenewError(this.silentRenewError);
    
    // Initial user load
    this.initializeUser();
  }

  private initializeUser = async () => {
    try {
      const user = await this.userManager.getUser();
      console.log('🔄 InitializeUser called, user from userManager:', user ? 'exists' : 'null');
      if (user && !user.expired) {
        this.user = user;
        console.log('🔄 Initial user loaded and stored:', {
          hasToken: !!user.access_token,
          tokenLength: user.access_token?.length || 0,
          expired: user.expired
        });

        // Prüfe auch bei der Initialisierung, ob der Benutzer in der Datenbank registriert ist
        try {
          const userService = await getUserService();
          await userService.registerOrUpdateCurrentUser();
          console.log('✅ Benutzer bei Initialisierung erfolgreich in Datenbank registriert/aktualisiert');
        } catch (error) {
          console.error('❌ Fehler bei der Benutzerregistrierung während Initialisierung:', error);
        }
      } else {
        console.log('⚠️ No valid user found during initialization, user expired:', user?.expired);
      }
    } catch (error) {
      console.error('❌ Failed to load initial user:', error);
    }
  };

  userLoaded = async (user: User) => {
    this.user = user;
    console.log('✅ Benutzer in AuthService geladen:', {
      hasToken: !!user.access_token,
      tokenLength: user.access_token?.length || 0,
      tokenStart: user.access_token?.substring(0, 50) + '...',
      profile: user.profile
    });

    // Automatische Benutzerregistrierung/Aktualisierung in der Datenbank
    try {
      const userService = await getUserService();
      await userService.registerOrUpdateCurrentUser();
      console.log('✅ Benutzer erfolgreich in Datenbank registriert/aktualisiert');
    } catch (error) {
      console.error('❌ Fehler bei der Benutzerregistrierung:', error);
      // Fehler wird nicht weitergeworfen, da die Anmeldung trotzdem funktionieren soll
    }
  };

  userUnloaded = () => {
    this.user = null;
    console.log('Benutzer abgemeldet');
  };

  accessTokenExpiring = () => {
    console.log('Access Token läuft ab');
  };

  accessTokenExpired = () => {
    console.log('Access Token abgelaufen');
    this.signinRedirect();
  };

  silentRenewError = (e: Error) => {
    console.log('Silent Renew Fehler:', e.message);
  };

  public getUser = async (): Promise<User | null> => {
    return await this.userManager.getUser();
  };

  public login = (): Promise<void> => {
    return this.userManager.signinRedirect();
  };

  public renewToken = async (): Promise<User | null> => {
    try {
      return await this.userManager.signinSilent();
    } catch (error) {
      console.error('Token renewal failed:', error);
      return null;
    }
  };

  public logout = (): Promise<void> => {
    return this.userManager.signoutRedirect();
  };

  public completeAuthentication = async (): Promise<User> => {
    const user = await this.userManager.signinRedirectCallback();
    
    // Nach erfolgreichem Login den Benutzer in der Datenbank registrieren
    try {
      const userService = await getUserService();
      await userService.registerOrUpdateCurrentUser();
      console.log('✅ Benutzer nach Login-Callback erfolgreich in Datenbank registriert/aktualisiert');
    } catch (error) {
      console.error('❌ Fehler bei der Benutzerregistrierung nach Login-Callback:', error);
    }
    
    return user;
  };

  public getAccessToken = (): string | null => {
    console.log('🔍 getAccessToken called, user:', this.user ? 'exists' : 'null');
    if (this.user) {
      console.log('🔍 User details:', {
        hasToken: !!this.user.access_token,
        expired: this.user.expired,
        tokenStart: this.user.access_token?.substring(0, 50) + '...'
      });
    }
    const token = this.user ? this.user.access_token : null;
    console.log('🔍 Token result:', token ? `${token.substring(0, 50)}...` : 'null');
    return token;
  };

  public getAccessTokenAsync = async (): Promise<string | null> => {
    try {
      const user = await this.userManager.getUser();
      return user ? user.access_token : null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  public isAuthenticated = (): boolean => {
    return this.user != null && !this.user.expired;
  };

  public hasRole = (role: string): boolean => {
    if (!this.user || !this.user.profile.groups) {
      return false;
    }
    const groups = this.user.profile.groups as string[];
    return groups.includes(role);
  };

  public isAdmin = (): boolean => {
    return this.hasRole('telefonbuch_admin');
  };

  public isViewer = (): boolean => {
    return this.hasRole('telefonbuch_viewer') || this.isAdmin();
  };

  public getUserProfile = () => {
    return this.user ? this.user.profile : null;
  };

  public signinRedirect = () => {
    localStorage.setItem('redirectUri', window.location.pathname);
    return this.userManager.signinRedirect();
  };
}

const authService = new AuthService();
export default authService;
